import { useEffect, useRef, useState } from 'react';
import { Button, Select, Space, Spin, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ContactListItem } from '@crm/shared';
import { api } from '../api/client';
import { contactName } from '../features/contacts/api';
import { QuickCreateModal } from './QuickCreateModal';

interface Props {
  value?: string | null;
  onChange?: (v: string | null, contact?: ContactListItem) => void;
  disabled?: boolean;
  organizationId?: string | null;
  /** Show the + button that creates a contact (quick create modal) and selects it. Default on. */
  quickCreate?: boolean;
}

/** Searchable contact picker, with a + button to create one. */
export function ContactSelect({ value, onChange, disabled, organizationId, quickCreate = true }: Props) {
  const [creating, setCreating] = useState(false);
  const [options, setOptions] = useState<{ value: string; label: string; contact?: ContactListItem }[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef(0);

  useEffect(() => {
    if (!value || options.some((o) => o.value === value)) return;
    api.get<ContactListItem>(`/contacts/${value}`).then((r) => setOptions((prev) => [{ value: r.data.id, label: contactName(r.data), contact: r.data }, ...prev.filter((o) => o.value !== value)])).catch(() => undefined);
  }, [value, options]);

  useEffect(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      setLoading(true);
      setFailed(false);
      try {
        const filter = organizationId ? JSON.stringify({ combinator: 'and', conditions: [{ kind: 'field', field: 'organization_id', op: 'eq', value: organizationId }] }) : undefined;
        const r = await api.get<{ items: ContactListItem[] }>('/contacts', { params: { q: search, pageSize: 15, sortBy: 'last_name', sortDir: 'asc', filter } });
        setOptions((prev) => {
          const found = r.data.items.map(toOption);
          const selected = prev.find((o) => o.value === value);
          return selected && !found.some((o) => o.value === selected.value) ? [selected, ...found] : found;
        });
      } catch {
        setFailed(true);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(timer.current);
  }, [search, value, organizationId]);

  const toOption = (c: ContactListItem) => ({ value: c.id, label: `${contactName(c)}${c.email ? ` <${c.email}>` : ''}`, contact: c });
  const select = (
    <Select
      style={{ flex: 1, minWidth: 0 }}
      showSearch
      allowClear
      disabled={disabled}
      value={value ?? undefined}
      placeholder="Search contacts"
      filterOption={false}
      onSearch={setSearch}
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : failed ? 'Could not load contacts. Try again.' : search ? 'No matching contacts' : 'No contacts'}
      options={options.map(({ value: v, label }) => ({ value: v, label }))}
      onChange={(v) => onChange?.((v as string | undefined) ?? null, options.find((o) => o.value === v)?.contact)}
    />
  );
  if (!quickCreate || disabled) return select;
  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        {select}
        <Tooltip title="New contact">
          <Button icon={<PlusOutlined />} aria-label="New contact" onClick={() => setCreating(true)} />
        </Tooltip>
      </Space.Compact>
      <QuickCreateModal
        objectType="CONTACT"
        open={creating}
        onClose={() => setCreating(false)}
        preset={organizationId ? { organizationId } : undefined}
        onCreated={(c) => {
          setOptions((prev) => [toOption(c), ...prev.filter((o) => o.value !== c.id)]);
          onChange?.(c.id, c);
        }}
      />
    </>
  );
}
